import { Button, Tooltip, Box, Alert } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import {
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
  DataGrid,
  GridRowParams,
  GridColumnHeaderParams,
} from '@mui/x-data-grid'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchDevicePage } from '@/api/devicePage'

import DeviceInfoButton from '@/components/DeviceInfoButton'
import type { FilterConfig, SelectedFilters } from '@/models/filters'
import type { Device } from '@/pages/Device/Device'
import types from '@/pages/Device/DeviceType'
import { IsOnline, Uid } from '@/pages/utils/formatter'
import { useFilters } from '@/hooks/useFilters'
import { FilterDrawer } from '@/components/FilterDrawer'

import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import { FetchDeviceFilters } from '@/api/definition'
import { useTableUrlState } from '@/hooks/useTableUrlState'

export interface DeviceData {
  uid: string
  type: string
  serial?: string
  label?: string
}

export interface ColumnConfig {
  includeProjectName?: boolean
  customColumns?: GridColDef[]
}

export interface ValidationConfig {
  validateSelection?: (device: Device) => boolean
  isRowSelectable?: (params: GridRowParams) => boolean
  validationErrorMessage?: string
}

export interface SharingConfig {
  shareFilters?: boolean
  shareSearch?: boolean
  enableFilters?: boolean
  enableSearch?: boolean
}

type ButtonColor =
  'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
type ButtonVariant = 'text' | 'outlined' | 'contained'
type ExtendedButtonColor = 'inherit' | ButtonColor

interface ButtonPropsBase {
  text?: string
  tooltip?: string
  sx?: SxProps<Theme>
  disabled?: boolean
}

interface NextButtonProps extends ButtonPropsBase {
  color?: ButtonColor
  variant?: ButtonVariant
}

interface ExtraButtonProps extends ButtonPropsBase {
  color?: ButtonColor
  variant?: ButtonVariant
  onClick?: () => void
}

interface LeftButtonProps extends Omit<ButtonPropsBase, 'sx'> {
  color?: ExtendedButtonColor
  variant?: ButtonVariant
  onClick?: () => void
}

export interface DeviceSelectionTableProps {
  initialDevices: Device[]
  rowCount: number
  selectedDeviceIds: string[]
  onSelectionChange: (newlySelected: DeviceData[], deselected: string[]) => void
  onNext: () => void
  onRowClick?: (device: Device) => boolean | void
  loaderError?: string
  deviceFilters?: FetchDeviceFilters
  pickTestProgress?: boolean
  columnConfig?: ColumnConfig
  validationConfig?: ValidationConfig
  nextButtonProps?: NextButtonProps
  extraButtonProps?: ExtraButtonProps
  leftButtonProps?: LeftButtonProps
  filterConfig?: FilterConfig
  sharingConfig?: SharingConfig
}

type DeviceTypeMap = Record<string, string>

const DeviceSelectionTable: React.FC<DeviceSelectionTableProps> = ({
  selectedDeviceIds,
  onSelectionChange,
  onNext,
  onRowClick,
  loaderError,
  deviceFilters,
  pickTestProgress = false,
  columnConfig = {},
  validationConfig = {},
  nextButtonProps = {},
  extraButtonProps = {},
  leftButtonProps = {},
  filterConfig,
  sharingConfig = {
    enableSearch: true,
    enableFilters: false,
    shareFilters: false,
    shareSearch: false,
  },
}) => {
  const { t } = useTranslation(['di', 'app', 'common'])
  const { fetchDevicePage } = useFetchDevicePage()
  // Use URL state hook
  const { urlState, updateUrlState } = useTableUrlState()
  const [localSearchValue, setLocalSearchValue] = useState(urlState.searchTerm)
  const [deviceData, setDeviceData] = useState<Device[]>([])
  const [countOfSelectedDevice, setCountOfSelectedDevice] = useState(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(loaderError ?? null)
  const abortController = useRef(new AbortController())
  const filterHooks = useFilters()
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  //Single fetch function
  const fetchData = useCallback(
    async (
      page: number,
      size: number,
      searchFilter: string,
      sortModelParam: GridSortModel,
      filters: SelectedFilters,
    ) => {
      abortController.current.abort()
      abortController.current = new AbortController()

      setIsLoading(true)
      setError(null)

      try {
        const sortBy =
          sortModelParam.length > 0
            ? [
                {
                  id: sortModelParam[0].field,
                  desc: sortModelParam[0].sort === 'desc',
                },
              ]
            : []

        if (!deviceFilters) return

        const result = await fetchDevicePage(
          deviceFilters,
          {
            pageIndex: page,
            pageSize: size,
            sortBy,
            globalFilter: searchFilter,
            selectedFilters: filters,
          },
          pickTestProgress,
        )

        const transformedData = result.data?.length
          ? result.data.map((device) => ({ ...device, id: device.uid }))
          : []

        const uniqueDevices = new Map<string, Device>()
        for (const device of transformedData) {
          uniqueDevices.set(device.uid, device)
        }

        setDeviceData(Array.from(uniqueDevices.values()))
        setCountOfSelectedDevice(result.rowCount ?? 0)
      } catch (error: unknown) {
        if (error instanceof Error && error.name !== 'AbortError') {
          setError(error.message ?? 'Failed to load data')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [fetchDevicePage, deviceFilters, pickTestProgress],
  )

  // SINGLE EFFECT: Fetch when URL state changes
  // This handles initial load AND browser back/forward
  useEffect(() => {
    fetchData(
      urlState.pageIndex,
      urlState.pageSize,
      urlState.searchTerm,
      urlState.sortModel,
      urlState.filters,
    )

    return () => {
      abortController.current.abort()
    }
  }, [
    urlState.pageIndex,
    urlState.pageSize,
    urlState.searchTerm,
    urlState.sortModel,
    urlState.filters,
    fetchData,
  ])

  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      updateUrlState({
        pageIndex: params.page,
        pageSize: params.pageSize,
      })
    },
    [updateUrlState],
  )

  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      updateUrlState({
        pageIndex: 0, // Reset to first page
        sortModel: newSortModel,
      })
    },
    [updateUrlState],
  )

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Immediate update for responsive typing
      setLocalSearchValue(newSearchTerm)

      // Debounced URL update for actual search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
        updateUrlState({
          pageIndex: 0,
          searchTerm: newSearchTerm,
        })
      }, 300)
    },
    [updateUrlState],
  )
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const handleFilterChange = useCallback(
    (
      groupId: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ) => {
      const newFilters = { ...urlState.filters }

      if (!newFilters[groupId]) {
        newFilters[groupId] = {}
      }

      if (isSelected) {
        newFilters[groupId][optionKey] = optionLabel
      } else {
        delete newFilters[groupId][optionKey]
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      }

      updateUrlState({
        pageIndex: 0, // Reset to first page
        filters: newFilters,
      })
    },
    [urlState.filters, updateUrlState],
  )

  const handleClearAllFilters = useCallback(() => {
    updateUrlState({
      pageIndex: 0,
      filters: {},
    })
  }, [updateUrlState])

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentLabel = urlState.filters[groupId]?.[optionKey] || ''
      handleFilterChange(groupId, optionKey, currentLabel, false)
    },
    [urlState.filters, handleFilterChange],
  )

  // Event handlers
  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      const device = params.row as Device
      if (onRowClick) {
        onRowClick(device)
      }
    },
    [onRowClick],
  )

  const handleSelectionModelChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      const newSelectionArray = newSelectionModel as string[]
      const currentSelectedIds = new Set(selectedDeviceIds)
      const newSelectionSet = new Set(newSelectionArray)
      const visibleIds = new Set(deviceData.map((d) => d.uid))

      // Helper: Check if device is newly selected
      const isNewlySelected = (id: string) =>
        newSelectionSet.has(id) && !currentSelectedIds.has(id)

      // Helper: Check if device is deselected
      const isDeselected = (id: string) =>
        !newSelectionSet.has(id) && currentSelectedIds.has(id)

      // Helper: Validate a device for selection
      const validateDevice = (device: Device | undefined) => {
        if (!device) return 'not-found'
        if (!validationConfig.validateSelection) return 'valid'
        return validationConfig.validateSelection(device) ? 'valid' : 'invalid'
      }

      // Categorize newly selected devices
      const newlySelectedIds: string[] = []
      const invalidSelections: string[] = []

      for (const id of visibleIds) {
        if (!isNewlySelected(id)) continue

        const device = deviceData.find((d) => d.uid === id)
        const validationResult = validateDevice(device)

        if (validationResult === 'valid') {
          newlySelectedIds.push(id)
        } else if (validationResult === 'invalid') {
          invalidSelections.push(id)
        }
      }

      // Get deselected IDs using filter (more declarative)
      const deselectedIds = Array.from(visibleIds).filter(isDeselected)

      // Convert newly selected IDs to DeviceData
      const newSelectedDevices: DeviceData[] = newlySelectedIds
        .map((id) => deviceData.find((d) => d.uid === id))
        .filter((device): device is Device => device !== undefined)
        .map((device) => ({
          uid: device.uid,
          type: String(device.type ?? 'unknown'),
          serial: device.serial ?? undefined,
          label: device.label ?? undefined,
        }))

      onSelectionChange(newSelectedDevices, deselectedIds)

      // Show validation error if needed
      const hasValidationError =
        invalidSelections.length > 0 && validationConfig.validationErrorMessage

      if (hasValidationError) {
        enqueueSnackbar(
          validationConfig.validationErrorMessage!.replace(
            '{count}',
            invalidSelections.length.toString(),
          ),
          { variant: 'warning' },
        )
      }
    },
    [deviceData, selectedDeviceIds, onSelectionChange, validationConfig],
  )

  // Column configuration
  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const columns: GridColDef[] = useMemo(() => {
    if (columnConfig.customColumns) {
      return columnConfig.customColumns
    }

    const baseColumns: GridColDef[] = [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <Uid value={params.value} />,
        width: 400,
        minWidth: 400,
        maxWidth: 400,
        flex: 0,
        resizable: false,
        sortable: true,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'isOnline',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <IsOnline value={params.value} />,
        flex: 1,
        minWidth: 80,
        sortable: true,
      },
    ]

    if (columnConfig.includeProjectName) {
      baseColumns.push({
        field: 'projectName',
        headerName: t('di:device.projectName'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      })
    }

    const deviceTypes = types as DeviceTypeMap
    baseColumns.push({
      field: 'type',
      headerName: t('di:device.type'),
      renderHeader: renderWrappedHeader,
      renderCell: (params) => {
        const deviceType = deviceTypes[params.value] ?? params.value
        const deviceRoleName = params.row.deviceRoleName

        if (deviceRoleName) {
          return (
            <Tooltip title={deviceRoleName} placement="top" arrow>
              <Box
                component="span"
                sx={{ display: 'inline-block', width: '100%' }}
              >
                {deviceType}
              </Box>
            </Tooltip>
          )
        }

        return deviceType
      },
      flex: 1,
      minWidth: 80,
      sortable: true,
    })

    if (pickTestProgress) {
      baseColumns.push({
        field: 'elapsedTime',
        headerName: t('device.durationTest.timeElapsed', {
          defaultValue: 'Time Elapsed',
        }),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => {
          const elapsedTime = params.row?.elapsedTime ?? ''
          return <span>{elapsedTime}</span>
        },
        flex: 1,
        minWidth: 60,
        sortable: false,
      })
    }

    baseColumns.push({
      field: 'action',
      headerName: '',
      flex: 0,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      resizable: false,
      sortable: false,
      renderCell: (params) => <DeviceInfoButton params={params} />,
    })

    return baseColumns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, columnConfig])

  // Should show custom toolbar if filters or search are enabled
  const shouldShowCustomToolbar =
    sharingConfig.enableFilters || sharingConfig.enableSearch

  return (
    <>
      {error && (
        <Alert
          data-testid="device-selection-error-alert"
          severity="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          width: '100%',
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.5',
          },
        }}
      >
        <DataGrid
          data-testid="device-grid-selection-table"
          rows={deviceData}
          getRowId={(row) => row.uid}
          autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
          disableVirtualization
          columns={columns}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          loading={isLoading}
          rowCount={countOfSelectedDevice}
          onRowClick={handleRowClick}
          paginationModel={{
            page: urlState.pageIndex,
            pageSize: urlState.pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={urlState.sortModel}
          onSortModelChange={handleSortModelChange}
          isRowSelectable={validationConfig.isRowSelectable}
          checkboxSelection
          rowSelectionModel={selectedDeviceIds}
          onRowSelectionModelChange={handleSelectionModelChange}
          keepNonExistentRowsSelected={true}
          slots={
            shouldShowCustomToolbar ? { toolbar: CustomToolbar } : undefined
          }
          slotProps={{
            toolbar: shouldShowCustomToolbar
              ? ({
                  onFilterClick: sharingConfig.enableFilters
                    ? filterHooks.openDrawer
                    : undefined,
                  activeFilterCount: sharingConfig.enableFilters
                    ? Object.values(urlState.filters).reduce(
                        (count, group) => count + Object.keys(group).length,
                        0,
                      )
                    : 0,
                  enableFilters: sharingConfig.enableFilters,
                  enableSearch: sharingConfig.enableSearch,
                  selectedFilters: urlState.filters,
                  filterConfig: filterConfig,
                  onRemoveFilter: handleRemoveFilter,
                  searchValue: localSearchValue,
                  onSearchChange: handleSearchChange,
                } as CustomToolbarProps)
              : undefined,
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              ActionsComponent: TablePaginationActions,
            },
          }}
          disableColumnFilter={true}
          disableColumnMenu={true}
          disableDensitySelector={true}
          disableColumnSelector={true}
          localeText={{ noRowsLabel: 'No records found' }}
        />
      </Box>

      {/* Filter Drawer */}
      {sharingConfig.enableFilters && filterConfig && (
        <FilterDrawer
          open={filterHooks.isDrawerOpen}
          onClose={filterHooks.closeDrawer}
          filterConfig={filterConfig}
          selectedFilters={urlState.filters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAllFilters}
        />
      )}

      <Box
        sx={{
          backgroundColor: '#eee',
          paddingTop: '24px',
          borderBottom: 'none !important',
          paddingBottom: '24px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 2,
          borderTop: '1px solid #ddd',
        }}
      >
        {/* Left aligned button */}
        {leftButtonProps?.text && (
          <Tooltip title={leftButtonProps.tooltip ?? ''} placement="top">
            <Box component="span">
              <Button
                data-testid="device-selection-left-button"
                variant={leftButtonProps.variant ?? 'outlined'}
                color={leftButtonProps.color ?? 'inherit'}
                onClick={leftButtonProps.onClick}
                disabled={leftButtonProps.disabled ?? false}
              >
                {leftButtonProps.text}
              </Button>
            </Box>
          </Tooltip>
        )}

        {/* Inline group to the right */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {extraButtonProps?.text && (
            <Tooltip title={extraButtonProps.tooltip ?? ''} placement="top">
              <Box component="span">
                <Button
                  data-testid="device-selection-extra-button"
                  variant={extraButtonProps.variant ?? 'contained'}
                  color={extraButtonProps.color ?? 'primary'}
                  sx={extraButtonProps.sx}
                  onClick={extraButtonProps.onClick}
                  disabled={extraButtonProps.disabled ?? false}
                >
                  {extraButtonProps.text}
                </Button>
              </Box>
            </Tooltip>
          )}

          {nextButtonProps?.text && (
            <Tooltip
              title={
                nextButtonProps.tooltip ??
                t('app:device.intake.willPutAllIntoService')
              }
              placement="top"
            >
              <Box component="span">
                <Button
                  data-testid="device-selection-next-button"
                  variant={nextButtonProps.variant ?? 'contained'}
                  color={nextButtonProps.color ?? 'primary'}
                  sx={nextButtonProps.sx}
                  onClick={onNext}
                  disabled={
                    nextButtonProps.disabled ?? selectedDeviceIds.length === 0
                  }
                >
                  {nextButtonProps.text}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </>
  )
}

export default DeviceSelectionTable
